import React from "react";

// shadcn/ui components
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Context provider
import { ItemDetailsProvider, useItemDetailsContext } from "./ItemDetailsContext";

// Sub-components (now using context - no props needed)
import ItemDetailsHeader from "./ItemDetailsHeader";
import ItemDetailsTitleSection from "./ItemDetailsTitleSection";
import ItemDetailsTagsSection from "./ItemDetailsTagsSection";
import ItemDetailsAssigneesSection from "./ItemDetailsAssigneesSection";
import ItemDetailsStatusSection from "./ItemDetailsStatusSection";
import ItemDetailsGoogleDriveSection from "./ItemDetailsGoogleDriveSection";
import ItemDetailsDescriptionSection from "./ItemDetailsDescriptionSection";
import ItemDetailsCommentsSection from "./ItemDetailsCommentsSection";
import { LoadingState } from "./CommonComponents";

// Existing components
import AggregatorView from "../aggregatorView";

/**
 * Inner content component that consumes context
 */
const ItemDetailsContent = () => {
    const {
        itemInfo,
        itemDetails,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        handleConfirmDelete,
        onAggregatorDataChange,
        itemType,
        handleClose,
        isOpen,
    } = useItemDetailsContext();

    return (
        <>
            <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
                <DialogContent
                    className="h-[85vh] w-[90vw] max-w-[1200px] p-0 flex flex-col overflow-hidden gap-0"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation(); // Prevent event from bubbling to FocusModeView
                            handleClose();
                        } else {
                            e.stopPropagation();
                        }
                    }}
                    showCloseButton={false}
                >
                    {/* Visually hidden title for accessibility */}
                    <DialogTitle className="sr-only">
                        {itemType === "folder" ? "Folder" : "Link"} Details
                    </DialogTitle>

                    {/* Header */}
                    <ItemDetailsHeader />

                    {/* Scrollable Content */}
                    <ScrollArea className="flex-1 h-full">
                        <div className="px-6 py-6 space-y-6">
                            {itemInfo ? (
                                <>
                                    {/* Hero Section: Title */}
                                    <div className="space-y-4">
                                        <ItemDetailsTitleSection />
                                    </div>

                                    <Separator />

                                    {/* Tags and Assignees side by side */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                        <ItemDetailsTagsSection />
                                        <ItemDetailsAssigneesSection />
                                    </div>

                                    {/* Status Section (below tags and assignees) */}
                                    <ItemDetailsStatusSection />

                                    <ItemDetailsGoogleDriveSection />
                                    <Separator />
                                    <ItemDetailsDescriptionSection />
                                    <Separator />
                                    {/* Aggregator View */}
                                    <AggregatorView
                                        folderId={itemDetails.id}
                                        data={itemInfo.aggregator_data}
                                        onDataChange={onAggregatorDataChange}
                                    />
                                    <ItemDetailsCommentsSection />
                                </>
                            ) : (
                                <LoadingState />
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this {itemType}? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

/**
 * Main component that wraps everything in the context provider
 */
const ItemDetailModal = ({
    isOpen,
    handleClose,
    depth,
    itemData,
    updateNameHandler,
    setItemData,
    on_delete,
    classes,
    href = null,
    itemInfo,
    fetchItemDetails,
    itemType,
}) => {
    return (
        <ItemDetailsProvider
            isOpen={isOpen}
            itemData={itemData}
            itemInfo={itemInfo}
            itemType={itemType}
            depth={depth}
            handleClose={handleClose}
            updateNameHandler={updateNameHandler}
            setItemData={setItemData}
            on_delete={on_delete}
            fetchItemDetails={fetchItemDetails}
            href={href}
        >
            <ItemDetailsContent />
        </ItemDetailsProvider>
    );
};

export default ItemDetailModal;
